<template>
  <v-app>
    <icon-font-wrapper>
      <!-- Topbar — plain header replaces v-toolbar so Vuetify's forced navy bg is gone -->
      <header class="sb-topbar">
        <!-- Text logo (SVG was white-on-transparent, unusable on light bg) -->
        <a :href="`${SbBaseUrl}`" class="sb-topbar-logo"
          >SPHERE<span class="sb-logo-accent">board</span></a
        >

        <!-- 1px separator -->
        <div class="sb-topbar-divider"></div>

        <!-- Page title (preserves sub-header behaviour) -->
        <span class="sb-topbar-title">
          <span>{{ $route.meta.title }}</span>
          <span v-if="!!subHeadingText"> : {{ subHeadingText }}</span>
          <span v-else-if="!!$route.meta.subHeader">
            : {{ $route.meta.subHeader }}
          </span>
        </span>

        <div class="sb-topbar-spacer"></div>

        <global-search-control @assetselected="getSelectedAsset" />

        <a
          id="user-guide"
          :href="`https://docs.sphereco.com/sphereboard-${quickHelpVersion}-user-guides`"
          class="sb-topbar-icon-btn"
          target="_blank"
          title="Open the user guide"
        >
          <span class="material-icons-outlined">help_outline</span>
        </a>

        <a
          id="request-support"
          href="https://customer.sphereco.com/"
          class="sb-topbar-icon-btn"
          target="_blank"
          title="Request Support"
        >
          <span class="material-icons-outlined">support_agent</span>
        </a>

        <!-- User dropdown -->
        <div class="sb-topbar-user">
          <button
            class="sb-user-btn"
            data-cy="sb-user-btn"
            aria-haspopup="true"
            :aria-expanded="isExpanded.toString()"
            @click.stop="isExpanded = !isExpanded"
            @keydown.esc.stop="isExpanded = false"
          >
            <span id="currentUser">{{ currentUser }}</span>
            <span class="material-icons-outlined">{{
              isExpanded ? 'expand_less' : 'expand_more'
            }}</span>
          </button>

          <div
            v-show="isExpanded"
            class="sb-user-dropdown"
            @click.stop
            @keydown.esc.stop="isExpanded = false"
          >
            <div class="sb-dd-header">
              <div class="sb-dd-name">{{ currentUser }}</div>
              <div class="sb-dd-role">{{ userRole }}</div>
            </div>

            <div class="sb-dd-row">
              <span>Theme</span>
              <div class="sb-theme-group">
                <button
                  :class="[
                    'sb-theme-btn',
                    themeMode === 'light' ? 'active' : '',
                  ]"
                  @click="setTheme('light')"
                >
                  Light
                </button>
                <!-- Auto mode — hidden until reintroduced in a future version
                <button
                  :class="['sb-theme-btn', themeMode === 'auto' ? 'active' : '']"
                  @click="setTheme('auto')"
                >Auto</button>
                -->
                <button
                  :class="[
                    'sb-theme-btn',
                    themeMode === 'dark' ? 'active' : '',
                  ]"
                  @click="setTheme('dark')"
                >
                  Dark
                </button>
              </div>
            </div>

            <div
              v-if="showLogout"
              class="sb-dd-signout"
              data-cy="sb-logout-btn"
              @click="logout"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign out
            </div>

            <div class="sb-dd-version">{{ sbVersion }}</div>
          </div>
        </div>
      </header>

      <!-- Left navigation sidebar -->
      <main-layout-sidebar v-if="!hideSidebar" ref="sidebar" />

      <!-- license alert -->
      <license-alert v-if="displayLicenseWarning" />

      <!-- Page content — padding driven by CSS (topbar + sidebar are plain HTML now) -->
      <v-content>
        <v-container
          fluid
          :class="
            ['IdentityMap', 'AccountInstances'].includes($route.name)
              ? 'pa-0'
              : 'pa-2'
          "
        >
          <the-offline-alert />
          <status-display-bar :error="errorMessage" />
          <v-dialog v-model="showIdleAlert" max-width="500px" persistent>
            <v-card>
              <v-card-text>
                <v-layout>
                  <v-flex>
                    Session has been idle for {{ appIdleAlertTime }} minutes.
                    Please click on OK to continue.
                  </v-flex>
                </v-layout>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <template>
                  <v-btn
                    color="success"
                    class="sb_btn_solid"
                    @click="closeIdleWarning"
                  >
                    OK
                  </v-btn>
                </template>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <div :class="wrapperClass">
            <!-- Router content -->
            <router-view />
          </div>
        </v-container>
      </v-content>
    </icon-font-wrapper>
  </v-app>
</template>

<script>
import TheOfflineAlert from '../components/TheOfflineAlert.vue';
import StatusDisplayBar from '../components/StatusDisplayBar.vue';
import IconFontWrapper from '../components/IconFontWrapper.vue';
import MainLayoutSidebar from './MainLayoutSidebar.vue';
import GlobalSearchControl from '../components/GlobalSearchControl.vue';
import { fetch } from '../utility/fetch.js';
import LicenseAlert from '../components/LicenseAlert.vue';
import Cookies from 'js-cookie';

export default {
  name: 'MainLayout',
  components: {
    TheOfflineAlert,
    StatusDisplayBar,
    IconFontWrapper,
    MainLayoutSidebar,
    GlobalSearchControl,
    LicenseAlert,
  },
  props: {
    title: {
      type: String,
      default: 'Layout Title',
    },
    isHomePage: {
      type: Boolean,
    },
    wrapperClass: { type: String, default: '' },
  },
  data() {
    return {
      menuHeading: '',
      menuToggleClass: false,
      subHeadingText: '',
      errorMessage: undefined,
      currentUser: 'Guest',
      userRole: '',
      quickHelpVersion: '6.0',
      assetType: '',
      showIdleAlert: false,
      appIdleAlertTime: 0,
      appIdleTimeOut: 0,
      hideSidebar: false,
      isExpanded: false,
      showLogout: true,
      // Two-mode theme: 'light' | 'dark'  (default: 'light')
      // Auto mode is temporarily hidden from the UI — to be reintroduced in a future version.
      // The underlying 'auto' logic (media-query CSS + html:not(.light):not(.dark)) is preserved.
      themeMode: (function () {
        var m = localStorage.getItem('sb-theme');
        return m === 'dark' ? 'dark' : 'light';
        /* auto mode — restore when reintroducing:
        return m === 'light' || m === 'dark' ? m : 'auto'; */
      })(),
      sbVersion: '',
      commitHash: typeof __COMMIT_HASH__ === 'undefined' ? '' : __COMMIT_HASH__,
    };
  },
  computed: {
    displayLicenseWarning() {
      if (!this.$store.getters.currentUserFetched) return false;
      if (!this.$store.getters.userIsAnalyst) return false;
      const meta = this.$route.meta;
      return !(
        meta.title == 'Asset Review Module' &&
        meta.hasOwnProperty('subHeader') &&
        meta.subHeader == 'Asset Review'
      );
    },
  },
  watch: {
    themeMode: {
      immediate: true,
      handler(mode) {
        const html = document.documentElement;
        html.classList.remove('light', 'dark');
        if (mode === 'light') html.classList.add('light');
        if (mode === 'dark') html.classList.add('dark');
        localStorage.setItem('sb-theme', mode);
      },
    },
  },
  async created() {
    const response = await fetch('/License/Get');
    if (response) {
      Cookies.set('license-json', JSON.stringify(response), { expires: 1 });
    }
    await this.$store.dispatch('LOAD_SITE_CONFIG_VALUES');
    // Fetch latest IdleTimeOut and IdleAlertTime values
    if (!this.$store.state.columnConfig.siteConfigFetchedFresh) {
      await this.$store.dispatch('LOAD_IDLE_CONFIG_VALUES');
    }
    // Access configured Idle AlertTime and Timeout values
    const timeoutConfig =
      this.$store.state.columnConfig.siteConfigValues['IdleTimeOut'];
    this.appIdleTimeOut = parseInt(timeoutConfig);
    const alertConfig =
      this.$store.state.columnConfig.siteConfigValues['IdleAlertTime'];
    this.appIdleAlertTime = parseInt(alertConfig);
    this.sphereAuthURL = await fetch(
      '/Home/GetAppConfig?v1=SphereAuth&v2=LoginURL'
    );
  },
  //eslint-disable-next-line complexity
  mounted() {
    localStorage.setItem('lastActivity', Date.now());
    this.$store.dispatch('FETCH_CURRENT_USER');
    let userUserName = document.getElementById('view-data:UserName');
    let userFullName = document.getElementById('view-data:User');
    if (userFullName !== null) {
      if (userFullName.value.includes(',')) {
        this.currentUser = userFullName.value.split(',').reverse().join(' ');
      } else {
        this.currentUser = userUserName.value;
      }
    }

    const userRoleElem = document.getElementById('view-data:UserRole');
    if (userRoleElem) {
      this.userRole = userRoleElem.value;
    }

    this.menuHeading = this.$route.meta.title;
    let emailFlag = this.$store.state.campaignDetails
      ? this.$store.state.campaignDetails.emailFlag
      : false;
    if (this.$route.meta.email != undefined || emailFlag) {
      this.hideSidebar = true;
    }

    this.$store.dispatch('FETCH_USER_CARDS').catch((error) => {
      this.errorMessage = `Unable get user cards: ${error.message}`;
      console.error(error);
    });
    this.$root.$on('change-sub-header', (item) => {
      this.subHeadingText = item;
    });

    const versionInputElem = document.getElementById('sb-version');
    if (versionInputElem) {
      const version = versionInputElem.value;
      this.sbVersion = this.commitHash
        ? `SPHEREboard v${version} (${this.commitHash.substr(0, 8)})`
        : `SPHEREboard v${version}`;
      let versionNumbers = version.split('.');
      this.quickHelpVersion = versionNumbers[0] + '-' + versionNumbers[1];
    }

    window.addEventListener('storage', (event) => {
      if (event.key === 'lastActivity') {
        this.showIdleAlert = false;
      }
      if (event.key === 'logout-event') {
        window.location.href = this.SbBaseUrl + '/Logout';
      }
    });

    // Close user dropdown when clicking anywhere outside it
    document.addEventListener('click', this.closeUserDropdown);

    this.handleSessionIdleTimeout();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeUserDropdown);
  },
  methods: {
    // ── Theme ──────────────────────────────────────────────────────────────
    setTheme(mode) {
      this.themeMode = mode;
    },
    closeUserDropdown() {
      this.isExpanded = false;
    },

    // ── Session timeout ────────────────────────────────────────────────────
    handleSessionIdleTimeout() {
      // Increment the idle time counter every minute.
      setInterval(() => {
        this.incrementTimer();
      }, 60000);

      // Heartbeat: keep the SPHERE.Auth token alive while the user is active.
      // Fires every 4 minutes — within SPHERE.Auth's 5-minute auto-refresh window.
      // Stops once the idle alert is showing (logout is imminent; no point refreshing).
      setInterval(() => {
        const lastActivity =
          Number(localStorage.getItem('lastActivity')) || Date.now();
        const idleMinutes = Math.floor((Date.now() - lastActivity) / 60000);
        const isActive =
          this.appIdleTimeOut === 0 || idleMinutes < this.appIdleTimeOut;
        if (isActive && !this.showIdleAlert) {
          fetch('/Home/GetAppConfig?v1=SphereAuth&v2=LoginURL').catch(() => {});
        }
      }, 4 * 60000);

      // When the tab becomes visible again (after sleep / background), immediately
      // fire a heartbeat so SPHERE.Auth can refresh the token before the next
      // real request hits a 401.
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !this.showIdleAlert) {
          const lastActivity =
            Number(localStorage.getItem('lastActivity')) || Date.now();
          const idleMinutes = Math.floor((Date.now() - lastActivity) / 60000);
          const isActive =
            this.appIdleTimeOut === 0 || idleMinutes < this.appIdleTimeOut;
          if (isActive) {
            fetch('/Home/GetAppConfig?v1=SphereAuth&v2=LoginURL').catch(
              () => {}
            );
          }
        }
      });

      // Reset the idle timer on mouse movement.
      this.$el.addEventListener('mousemove', this.resetTimer);
      this.$el.addEventListener('keypress', this.resetTimer);
    },
    async incrementTimer() {
      const lastActivity =
        Number(localStorage.getItem('lastActivity')) || Date.now();
      const idleMinutes = Math.floor((Date.now() - lastActivity) / 60000);
      if (
        this.appIdleAlertTime > 0 &&
        idleMinutes >= this.appIdleAlertTime &&
        !this.showIdleAlert
      ) {
        this.showIdleAlert = true;
      }
      if (this.appIdleTimeOut > 0 && idleMinutes >= this.appIdleTimeOut) {
        await this.logout();
      }
    },
    resetTimer() {
      if (!this.showIdleAlert) {
        localStorage.setItem('lastActivity', Date.now());
      }
    },
    closeIdleWarning() {
      this.showIdleAlert = false;
      localStorage.setItem('lastActivity', Date.now());
    },
    getSelectedAsset(assetType) {
      this.assetType = assetType;
    },

    async logout() {
      localStorage.setItem('logout-event', Date.now());
      window.location.href = this.SbBaseUrl + '/Logout';
    },
  },
};
</script>

<style>
@import '../static/css/SphereGlobal.css';
@import '../static/css/VuetifyOverrides.css';
@import '../static/css/Components/MainLayout.css';
@import '../static/css/Components/Panel.css';
</style>
